import {
  getAuthCookie,
  getSharedCookie,
  setSharedCookie,
  clearSharedCookie,
} from "@/src/utils/authCookie";
import type {
  AddItemPayload,
  CartServiceability,
  CartV3,
  CheckoutPayload,
  CheckoutResult,
  CodEligibilityResult,
  VerifyCartResult,
} from "@/src/types/cartV3";

/**
 * Cart session is persisted in a cross-subdomain cookie so a single cart
 * survives navigation between tenant storefronts (sugandhi.lvh.me →
 * luru.lvh.me). The previous localStorage key was origin-scoped, which
 * prevented multi-outlet ordering across brands.
 */
const SESSION_COOKIE_NAME = "cart_session_id";
const SESSION_TTL_SECONDS = 7 * 24 * 60 * 60; // 7 days, matches backend Redis TTL.

function baseUrl(): string {
  const url = process.env.NEXT_PUBLIC_PRODUCT_SERVICE_URL;
  if (!url) throw new Error("NEXT_PUBLIC_PRODUCT_SERVICE_URL is not configured");
  return url.replace(/\/+$/, "");
}

function readSessionId(): string | null {
  return getSharedCookie(SESSION_COOKIE_NAME);
}

function writeSessionId(sessionId: string): void {
  setSharedCookie(SESSION_COOKIE_NAME, sessionId, SESSION_TTL_SECONDS);
}

export function clearCartSession(): void {
  clearSharedCookie(SESSION_COOKIE_NAME);
}

interface ApiEnvelope<T> {
  success: boolean;
  message: string;
  data?: T;
  error?: unknown;
  statusCode: number;
}

interface FetchOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE" | "PUT";
  body?: unknown;
  auth?: boolean;
}

async function cartApi<T>(path: string, opts: FetchOptions = {}): Promise<T> {
  const { method = "GET", body, auth = false } = opts;
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  const sid = readSessionId();
  if (sid) headers["Sessionid"] = sid;

  if (auth) {
    const token = getAuthCookie();
    if (token) headers["Authorization"] = `Bearer ${token}`;
  }

  const res = await fetch(`${baseUrl()}/v3/buyer/cart${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  const returned = res.headers.get("Sessionid") || res.headers.get("sessionid");
  if (returned) writeSessionId(returned);

  const envelope = (await res.json().catch(() => null)) as ApiEnvelope<T> | null;
  if (!res.ok) {
    const message =
      (envelope && (envelope.message || (envelope.error as { message?: string })?.message)) ||
      `Request failed: ${res.status}`;
    const err = new Error(message) as Error & { status?: number; details?: unknown };
    err.status = res.status;
    err.details = envelope?.error;
    throw err;
  }
  if (!envelope || envelope.data === undefined) {
    throw new Error("Malformed cart response");
  }
  return envelope.data as T;
}

export async function getCart(): Promise<{ cart: CartV3; serviceability?: CartServiceability }> {
  // The envelope carries `serviceability` alongside `cart` when part of the
  // basket cannot reach the selected address. Returning the whole thing (rather
  // than just `.cart`) is what lets the drawer draw one banner instead of
  // reverse-engineering it from per-line reason strings.
  const data = await cartApi<{ cart: CartV3; serviceability?: CartServiceability }>("/");
  return { cart: data.cart, serviceability: data.serviceability };
}

/**
 * True when an error from any cart call is the delivery-area refusal. The
 * backend returns the same code from add-item, address change, verify and
 * checkout, so callers branch once.
 */
export function isLocationError(err: unknown): err is LocationError {
  const d = (err as { details?: { code?: string } })?.details;
  return d?.code === "LOCATION_NOT_SERVICEABLE";
}

export interface LocationError extends Error {
  status?: number;
  details: CartServiceability & { reason?: string; city?: string; pincode?: string };
}

export async function addItem(payload: AddItemPayload): Promise<CartV3> {
  const data = await cartApi<{ cart: CartV3 }>("/items", {
    method: "POST",
    body: payload,
  });
  return data.cart;
}

export async function updateQuantity(lineId: string, quantity: number): Promise<CartV3> {
  const data = await cartApi<{ cart: CartV3 }>(`/items/${encodeURIComponent(lineId)}/quantity`, {
    method: "PATCH",
    body: { quantity },
  });
  return data.cart;
}

export async function removeItem(lineId: string): Promise<CartV3> {
  const data = await cartApi<{ cart: CartV3 }>(`/items/${encodeURIComponent(lineId)}`, {
    method: "DELETE",
  });
  return data.cart;
}

export async function clearCart(): Promise<CartV3> {
  const data = await cartApi<{ cart: CartV3 }>("/", { method: "DELETE" });
  return data.cart;
}

export async function updateAddress(addressId: string): Promise<CartV3> {
  const data = await cartApi<{ cart: CartV3 }>("/address", {
    method: "PATCH",
    body: { addressId },
    auth: true,
  });
  return data.cart;
}

/**
 * Apply or remove the cart's coupon. `code: null` (or empty) removes whatever
 * coupon the cart holds — one coupon per cart, so there is nothing to key a
 * removal by.
 *
 * `outletId` is OPTIONAL and means "this is a seller's own code, scoped to that
 * store". Omit it for a GrabLocal-wide code: those belong to the cart, are
 * measured against the whole cart subtotal, and are split across outlets by the
 * server. Passing one anyway is safe — the server checks the scope it actually
 * resolved and still files a global coupon as global.
 */
export async function applyCoupon(
  outletId: string | null,
  code: string | null
): Promise<CartV3> {
  const data = await cartApi<{ cart: CartV3 }>("/coupon", {
    method: "PATCH",
    body: outletId ? { outletId, code } : { code },
    auth: true,
  });
  return data.cart;
}

export async function verifyCart(): Promise<VerifyCartResult> {
  return cartApi<VerifyCartResult>("/verify", {
    method: "POST",
    auth: true,
  });
}

/**
 * Ask the server whether this cart can go COD, and for what split.
 *
 * Deliberately un-caught. The eligibility service fails CLOSED on its own —
 * a courier outage comes back as `{ eligible: false, reason: 'SERVICEABILITY_
 * UNAVAILABLE' }`, not an exception — so a throw here is never "COD is
 * unavailable". It means the cart is missing/empty or the buyer isn't
 * authenticated, which is the caller's problem to surface, not something to
 * quietly swallow into a hidden COD tile.
 *
 * Needs both the Sessionid header (cart) and the Bearer token (buyer), hence
 * auth: true. GET is cartApi's default method, so there is nothing to override.
 */
export async function getCodEligibility(): Promise<CodEligibilityResult> {
  return cartApi<CodEligibilityResult>("/cod-eligibility", { auth: true });
}

export async function checkoutCart(payload: CheckoutPayload = {}): Promise<CheckoutResult> {
  return cartApi<CheckoutResult>("/checkout", {
    method: "POST",
    body: payload,
    auth: true,
  });
}
